import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Code2,
  FileCheck2,
  Handshake,
  LoaderCircle,
  Send,
  UserRound,
  WalletCards,
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageProvider';

const MAIL_ENDPOINT = 'https://api.wonya-soft.com/api/v1/communications/email';
const CONTACT_RECIPIENT = 'wonyasoft.sa@gmail.com';

const initialData = {
  category: '',
  name: '',
  organization: '',
  email: '',
  phone: '',
  subject: '',
  timeline: '',
  message: '',
  consent: false,
  website: '',
};

const steps = [
  { label: 'Besoin', icon: WalletCards },
  { label: 'Coordonnées', icon: UserRound },
  { label: 'Brief', icon: FileCheck2 },
  { label: 'Vérification', icon: CheckCircle2 },
];

const projectCategories = [
  {
    value: 'api',
    icon: Code2,
    title: 'Intégration et API',
    text: 'API Mobile Money, documentation et accompagnement technique',
  },
  {
    value: 'payments',
    icon: WalletCards,
    title: 'Paiements et solutions',
    text: 'BulkPay, encaissement, application ou solution monétique',
  },
  {
    value: 'advisory',
    icon: Handshake,
    title: 'Projet et accompagnement',
    text: 'Développement sur mesure, partenariat ou besoin spécifique',
  },
];

const subjectsByCategory = {
  api: [
    'Intégration API Mobile Money',
    'Documentation ou assistance API',
    'Support technique',
  ],
  payments: [
    'BulkPay - Paiements en masse',
    'Encaissement et paiement numérique',
    'Application mobile WonyaPay',
  ],
  advisory: [
    'Développement sur mesure',
    'Demande commerciale ou partenariat',
    'Autre projet',
  ],
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const createContactEmail = (data) => {
  const rows = [
    ['Catégorie', projectCategories.find(({ value }) => value === data.category)?.title],
    ['Sujet', data.subject],
    ['Nom', data.name],
    ['Organisation', data.organization || 'Non précisée'],
    ['Email', data.email],
    ['Téléphone', data.phone],
    ['Délai envisagé', data.timeline],
  ];

  return `
    <div style="background:#f4f5f7;padding:32px 16px;font-family:Arial,sans-serif;color:#111827">
      <div style="max-width:680px;margin:auto;background:#fff;border:1px solid #e5e7eb;border-radius:22px;overflow:hidden">
        <div style="padding:28px 32px;background:linear-gradient(135deg,#c81d25,#8f1118);color:#fff">
          <div style="font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;opacity:.85">WonyaSoft · Contact</div>
          <h1 style="margin:9px 0 0;font-size:25px">Nouvelle demande de projet</h1>
        </div>
        <div style="padding:30px">
          <table role="presentation" style="width:100%;border-collapse:collapse;font-size:14px">
            ${rows.map(([label, value]) => `
              <tr>
                <td style="padding:10px 0;color:#6b7280;width:190px;border-bottom:1px solid #f0f1f3">${escapeHtml(label)}</td>
                <td style="padding:10px 0;font-weight:700;border-bottom:1px solid #f0f1f3">${escapeHtml(value)}</td>
              </tr>`).join('')}
          </table>
          <div style="margin-top:24px;font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#a4141b">Brief</div>
          <div style="margin-top:10px;padding:18px;background:#f9fafb;border-left:3px solid #c81d25;border-radius:0 12px 12px 0;line-height:1.7">
            ${escapeHtml(data.message).replace(/\n/g, '<br>')}
          </div>
        </div>
      </div>
    </div>`;
};

const ContactRequestWizard = () => {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const panelRef = useRef(null);

  const updateField = ({ target }) => {
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setData((current) => ({ ...current, [target.name]: value }));
    setErrors((current) => ({ ...current, [target.name]: '' }));
  };

  const chooseCategory = (category) => {
    setData((current) => ({ ...current, category, subject: '' }));
    setErrors({});
  };

  const requiredFields = () => {
    if (step === 0) return ['category'];
    if (step === 1) return ['name', 'email', 'phone'];
    if (step === 2) return ['subject', 'timeline', 'message'];
    return ['consent'];
  };

  const validateStep = () => {
    const nextErrors = {};
    requiredFields().forEach((field) => {
      if (!data[field]) nextErrors[field] = t('Ce champ est requis.');
    });
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      nextErrors.email = t('Saisissez une adresse email valide.');
    }
    setErrors(nextErrors);
    const firstError = Object.keys(nextErrors)[0];
    if (firstError) {
      window.requestAnimationFrame(() => document.querySelector(`#contact-form [name="${firstError}"]`)?.focus());
    }
    return !firstError;
  };

  const nextStep = () => {
    if (validateStep()) setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const previousStep = () => {
    setErrors({});
    setStep((current) => Math.max(current - 1, 0));
  };

  useEffect(() => {
    panelRef.current?.focus({ preventScroll: true });
  }, [step]);

  const submit = async () => {
    if (!validateStep()) return;
    setStatus('submitting');
    try {
      if (data.website) {
        setStatus('success');
        return;
      }
      const response = await fetch(MAIL_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: CONTACT_RECIPIENT,
          subject: `[Contact WonyaSoft] ${data.subject} — ${data.organization || data.name}`,
          html: createContactEmail(data),
        }),
      });
      const result = await response.json();
      const accepted = result?.data?.data?.success === true;
      setStatus(response.ok && (result.success === true || accepted) ? 'success' : 'error');
    } catch (error) {
      console.error("Erreur lors de l'envoi du contact:", error);
      setStatus('error');
    }
  };

  const reset = () => {
    setData(initialData);
    setErrors({});
    setStatus('idle');
    setStep(0);
  };

  const fieldError = (name) => errors[name]
    ? <span className="ws-wizard__error" id={`contact-${name}-error`}>{errors[name]}</span>
    : null;

  if (status === 'success') {
    return (
      <div id="contact-form" className="ws-contact-request">
        <div className="ws-section-heading">
          <span className="ws-kicker">{t('Votre projet')}</span>
          <h2 id="ws-contact-form-title">{t('Décrivez le besoin, nous cadrons la suite')}</h2>
        </div>
        <div className="ws-wizard ws-wizard--success" role="status">
          <span className="ws-wizard-success__icon"><CheckCircle2 size={38} aria-hidden="true" /></span>
          <span className="ws-kicker">{t('Demande transmise')}</span>
          <h3>{t('Votre demande de projet a bien été envoyée.')}</h3>
          <p>{t('Notre équipe examinera votre brief et vous contactera pour organiser la prochaine étape.')}</p>
          <button className="ws-button ws-button--secondary" type="button" onClick={reset}>{t('Envoyer une autre demande')}</button>
        </div>
      </div>
    );
  }

  return (
    <div id="contact-form" className="ws-contact-request">
      <div className="ws-section-heading">
        <span className="ws-kicker">{t('Votre projet')}</span>
        <h2 id="ws-contact-form-title">{t('Décrivez le besoin, nous cadrons la suite')}</h2>
        <p>{t('Un parcours rapide pour transmettre un brief clair à notre équipe.')}</p>
      </div>

      <div className="ws-wizard ws-contact-wizard">
        <ol className="ws-wizard-progress" aria-label={t('Progression de la demande')}>
          {steps.map(({ label, icon: Icon }, index) => (
            <li className={index === step ? 'is-current' : index < step ? 'is-complete' : ''} key={label} aria-current={index === step ? 'step' : undefined}>
              <span>{index < step ? <Check size={18} aria-hidden="true" /> : <Icon size={18} aria-hidden="true" />}</span>
              <small>{t(label)}</small>
            </li>
          ))}
        </ol>

        <div className="ws-wizard__panel" ref={panelRef} tabIndex="-1">
          <div className="ws-wizard__step-count">{t('Étape')} {step + 1} / {steps.length}</div>

          {step === 0 && (
            <>
              <div className="ws-wizard__heading">
                <h3>{t('Quel est le motif de votre demande ?')}</h3>
                <p>{t('Choisissez l’une des options ci-dessous pour nous indiquer comment nous pouvons vous aider.')}</p>
              </div>
              <div className="ws-wizard-profile-grid ws-contact-project-grid">
                {projectCategories.map(({ value, icon: Icon, title, text }) => (
                  <button
                    className={data.category === value ? 'ws-wizard-profile is-selected' : 'ws-wizard-profile'}
                    type="button"
                    name="category"
                    aria-pressed={data.category === value}
                    onClick={() => chooseCategory(value)}
                    key={value}
                  >
                    <span><Icon size={27} aria-hidden="true" /></span>
                    <strong>{t(title)}</strong>
                    <small>{t(text)}</small>
                    <i><Check size={16} aria-hidden="true" /></i>
                  </button>
                ))}
              </div>
              {fieldError('category')}
            </>
          )}

          {step === 1 && (
            <>
              <div className="ws-wizard__heading">
                <h3>{t('Comment pouvons-nous vous recontacter ?')}</h3>
                <p>{t('Indiquez les coordonnées de la personne qui suit le projet.')}</p>
              </div>
              <div className="ws-wizard-fields">
                <label className="ws-field">
                  <span>{t('Nom complet')} *</span>
                  <input name="name" value={data.name} onChange={updateField} autoComplete="name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'contact-name-error' : undefined} placeholder={t('Ex. Jean Mukendi')} />
                  {fieldError('name')}
                </label>
                <label className="ws-field">
                  <span>{t('Organisation')}</span>
                  <input name="organization" value={data.organization} onChange={updateField} autoComplete="organization" placeholder={t('Entreprise ou institution')} />
                </label>
                <label className="ws-field">
                  <span>{t('Adresse email')} *</span>
                  <input name="email" type="email" value={data.email} onChange={updateField} autoComplete="email" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'contact-email-error' : undefined} placeholder="jean@entreprise.com" />
                  {fieldError('email')}
                </label>
                <label className="ws-field">
                  <span>{t('Téléphone')} *</span>
                  <input name="phone" type="tel" value={data.phone} onChange={updateField} autoComplete="tel" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'contact-phone-error' : undefined} placeholder="+243 XXX XXX XXX" />
                  {fieldError('phone')}
                </label>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="ws-wizard__heading">
                <h3>{t('Donnez-nous le contexte essentiel')}</h3>
                <p>{t('Quelques informations précises suffisent pour préparer le premier échange.')}</p>
              </div>
              <div className="ws-wizard-fields">
                <label className="ws-field">
                  <span>{t('Sujet principal')} *</span>
                  <select name="subject" value={data.subject} onChange={updateField} aria-invalid={Boolean(errors.subject)} aria-describedby={errors.subject ? 'contact-subject-error' : undefined}>
                    <option value="">{t('Sélectionnez une catégorie')}</option>
                    {(subjectsByCategory[data.category] || []).map((subject) => <option value={subject} key={subject}>{t(subject)}</option>)}
                  </select>
                  {fieldError('subject')}
                </label>
                <label className="ws-field">
                  <span>{t('Délai envisagé')} *</span>
                  <select name="timeline" value={data.timeline} onChange={updateField} aria-invalid={Boolean(errors.timeline)} aria-describedby={errors.timeline ? 'contact-timeline-error' : undefined}>
                    <option value="">{t('Sélectionnez')}</option>
                    <option value="Dès que possible">{t('Dès que possible')}</option>
                    <option value="Dans un à trois mois">{t('Dans un à trois mois')}</option>
                    <option value="Plus tard ou à définir">{t('Plus tard ou à définir')}</option>
                  </select>
                  {fieldError('timeline')}
                </label>
                <label className="ws-field ws-field--wide">
                  <span>{t('Décrivez votre besoin')} *</span>
                  <textarea name="message" value={data.message} onChange={updateField} rows="6" aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'contact-message-error' : undefined} placeholder={t('Présentez votre activité, le parcours attendu, les volumes et votre délai souhaité.')} />
                  {fieldError('message')}
                  <small>{t('N’incluez jamais de mot de passe, de code PIN ou de secret API.')}</small>
                </label>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="ws-wizard__heading">
                <h3>{t('Vérifiez votre demande')}</h3>
                <p>{t('Relisez votre brief avant son envoi à l’équipe WonyaSoft.')}</p>
              </div>
              <dl className="ws-wizard-summary">
                <div><dt>{t('Projet')}</dt><dd>{t(projectCategories.find(({ value }) => value === data.category)?.title || '')}</dd></div>
                <div><dt>{t('Sujet principal')}</dt><dd>{t(data.subject)}</dd></div>
                <div><dt>{t('Contact')}</dt><dd>{data.name}{data.organization && <><br />{data.organization}</>}</dd></div>
                <div><dt>{t('Coordonnées')}</dt><dd>{data.email}<br />{data.phone}</dd></div>
                <div><dt>{t('Délai')}</dt><dd>{t(data.timeline)}</dd></div>
                <div><dt>{t('Brief')}</dt><dd className="ws-wizard-summary__brief">{data.message}</dd></div>
              </dl>
              <label className="ws-wizard-consent">
                <input name="consent" type="checkbox" checked={data.consent} onChange={updateField} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'contact-consent-error' : undefined} />
                <span>{t('J’accepte que WonyaSoft utilise ces informations uniquement pour traiter cette demande et me recontacter.')}</span>
              </label>
              {fieldError('consent')}
              <div className="ws-contact-honeypot" aria-hidden="true">
                <label htmlFor="contact-request-website">Site web</label>
                <input id="contact-request-website" name="website" value={data.website} onChange={updateField} tabIndex="-1" autoComplete="off" />
              </div>
            </>
          )}

          {status === 'error' && (
            <p className="ws-wizard-submit-error" role="alert">
              {t('L’envoi a échoué. Vérifiez votre connexion puis réessayez, ou contactez-nous par WhatsApp.')}
            </p>
          )}

          <div className="ws-wizard-actions">
            {step > 0 && (
              <button className="ws-button ws-button--ghost" type="button" onClick={previousStep} disabled={status === 'submitting'}>
                <ArrowLeft size={18} aria-hidden="true" /> {t('Retour')}
              </button>
            )}
            {step < steps.length - 1 ? (
              <button className="ws-button ws-button--primary" type="button" onClick={nextStep}>
                {t('Continuer')} <ArrowRight size={18} aria-hidden="true" />
              </button>
            ) : (
              <button className="ws-button ws-button--primary" type="button" onClick={submit} disabled={status === 'submitting'}>
                {status === 'submitting'
                  ? <><LoaderCircle className="ws-wizard-spinner" size={18} aria-hidden="true" /> {t('Envoi en cours…')}</>
                  : <><Send size={18} aria-hidden="true" /> {t('Envoyer la demande')}</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactRequestWizard;
